import CloseIcon from '@mui/icons-material/Close';
import {
  Typography,
  Box,
  useMediaQuery,
  Button,
  CircularProgress,
  IconButton,
  Stack,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';

import ShieldIconContainer from '@assets/icons/shield-icon-container.svg';
import {
  CloudFunctionsService,
  FirebaseService,
  rudderstackService,
} from '@services';
import { useStore } from '@stores';
import { globalTheme as theme } from '@styles';
import { STATUS_OPTIONS } from '@types';

import { WinBackSavingsCard } from './WinBackSavingsCard';

export const ConfirmExit = observer(() => {
  const { t } = useTranslation('footer');
  const { userStore, commonStore, formStore, decisionEngineStore } = useStore();
  const { shortFormLead, resultsCtaAction, buttonLoading } = commonStore;
  const hasShortFormLead = Object.keys(shortFormLead).length;
  // Matches the breakpoint FormModal uses to decide bottom-sheet vs. full-modal.
  const isDesktop = !useMediaQuery('(max-width:960px)');

  const { exit } = formStore;

  /**
   * The results-page variant: different copy, a Good Fit savings card, and a
   * primary CTA that continues the results flow instead of just dismissing.
   * Gated on the results module having a recommended program, so the
   * "your savings are waiting" promise is never shown to an unqualified user.
   */
  const isResultsVariant =
    formStore.currentModuleName === 'MODULE_RESULT_OPTIONS' &&
    !!decisionEngineStore.recommendedProgram;

  const cloud = new CloudFunctionsService();
  const firebase = new FirebaseService(cloud);

  const handleExit = async () => {
    rudderstackService.workflowExitConfirmed();

    let data = {};
    if (hasShortFormLead && userStore.user) {
      await firebase.updateEngagementStatus(
        userStore.user,
        STATUS_OPTIONS.USER_ABANDONED
      );

      if (commonStore.shortFormLead.Status?.toLowerCase() === 'success') {
        data = {
          leadid: commonStore.shortFormLead.LeadId,
          leadguid: commonStore.shortFormLead.LeadGUID,
        };
      } else {
        data = {
          error: commonStore.shortFormLead,
        };
      }
    }
    exit(data);
    formStore.setToggleRampingOff();
  };

  /**
   * Results variant: run the same submit as the results screen's primary CTA
   * ("Personalize to Review Plan Details"). The modal deliberately stays open
   * while that runs — the button becomes a spinner and the redirect (or the
   * host's takeover) is what ends the wait, so the user never sees the results
   * page flash by mid-submit. Base variant: just dismiss.
   * Falls back to dismissing if the results footer isn't mounted to register it.
   */
  const handlePrimary = async () => {
    if (isResultsVariant && resultsCtaAction) {
      // No tracking here: the delegated results submit fires its own
      // button_click and, for opted-in users, digital_enrollment_opted_in.
      await resultsCtaAction();
      return;
    }
    rudderstackService.buttonClick(t('confirmExit.completeToViewOptions'));
    formStore.setToggleRampingOff();
  };

  // Shared with the results footer button, so the win-back spinner tracks the
  // very same submit lifecycle rather than a second, local notion of "busy".
  const isSubmitting = isResultsVariant && buttonLoading;

  const exitWithoutSaving = (
    <Button
      variant="text"
      data-cy="confirmExit-confirm"
      onClick={() => handleExit()}
      disabled={isSubmitting}
      sx={{
        height: '37px',
        borderRadius: '4px',
        padding: '8px 24px',
        textTransform: 'none',
        color: theme.palette.brandGreen.dark,
        lineHeight: '20.8px',
        whiteSpace: 'nowrap',
        // The explicit colour above also applies while disabled, so state the
        // disabled colour too — otherwise it still reads as clickable.
        '&.Mui-disabled': { color: theme.palette.brandGray.main },
      }}
    >
      {t('confirmExit.exitWithoutSaving')}
    </Button>
  );

  const primaryButton = (
    <Button
      variant="contained"
      data-cy="confirmExit-decline"
      onClick={() => handlePrimary()}
      disabled={isSubmitting}
      sx={{
        height: '48px',
        width: { xs: '100%', md: isResultsVariant ? '334px' : '321px' },
        borderRadius: '4px',
        padding: '14px 24px',
        textTransform: 'none',
        lineHeight: '20px',
        boxShadow: theme.customShadows.button,
        whiteSpace: 'nowrap',
      }}
    >
      {isSubmitting ? (
        // Matches the results screen's CTA while submitting: the disabled
        // (greyed) contained button with a white spinner over it.
        <Box display="flex" alignItems="center" justifyContent="center">
          <CircularProgress
            data-cy="confirmExit-loading"
            size={24}
            sx={{ color: theme.palette.common.white }}
          />
        </Box>
      ) : (
        t(
          isResultsVariant
            ? 'confirmExit.continueToMonthlyPayments'
            : 'confirmExit.completeToViewOptions'
        )
      )}
    </Button>
  );

  // The with-plan frames are tighter: 37px stack gaps and a 40px desktop top
  // pad vs. 75px/80px on the base frames.
  const desktopTopPad = isResultsVariant ? '40px' : '80px';
  /**
   * Mobile stack gaps, fluid instead of fixed. Three fixed gaps (75px, or 37px
   * on the results frame) plus the icon give the base frame a ~635px intrinsic
   * height; a phone whose visible viewport is shorter than that pushes the
   * buttons below the fold no matter how tall the sheet is allowed to be.
   * These track the viewport instead: full design size at the ~847px the frame
   * was drawn for, shrinking proportionally below it so the buttons stay in
   * view down to a ~520px viewport. Past that, overflowY still scrolls.
   */
  const mobileGap = (unit: 'vh' | 'dvh') =>
    isResultsVariant
      ? `clamp(16px, calc(6.3${unit} - 16px), 37px)`
      : `clamp(20px, calc(12.84${unit} - 34px), 75px)`;
  const stackGap = 'var(--winback-gap)';
  // The results frame carries an extra savings card, so it overflows a laptop
  // viewport at the base sizes: shrink the icon 10% and tighten the title's
  // leading to claw back the ~24px that produced the scrollbar.
  const iconSize = isResultsVariant ? '122px' : '136px';
  const desktopTitleLineHeight = isResultsVariant ? '36.8px' : '41.6px';

  return (
    <Box
      id="module-confirm-exit"
      data-cy={isResultsVariant ? 'winback-results' : 'winback-base'}
      sx={{
        zIndex: 1001,
        backgroundColor: theme.palette.common.white,
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'auto',
        // Mobile: bottom sheet with rounded top corners. Desktop: fills the modal body.
        ...(isDesktop
          ? { height: '100%' }
          : {
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              // Sized to content rather than a fraction of the viewport: the
              // frame has a fixed intrinsic height, so any fraction small
              // enough for a real phone's visible viewport pushed the buttons
              // below the fold. The cap is only a ceiling -- dvh tracks the
              // visible viewport, where vh counts the browser chrome too.
              height: 'auto',
              // The sheet may run to the very top of the screen when its
              // content needs the room, so nothing is reserved above it.
              maxHeight: '100vh',
              '--winback-gap': mobileGap('vh'),
              '@supports (height: 100dvh)': {
                maxHeight: '100dvh',
                '--winback-gap': mobileGap('dvh'),
              },
              borderTopLeftRadius: '24px',
              borderTopRightRadius: '24px',
              boxShadow: theme.customShadows.bottomSheet,
            }),
      }}
    >
      {!isDesktop && (
        <Box sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              height: '52px',
              px: '19px',
            }}
          >
            <IconButton
              size="small"
              disabled={isSubmitting}
              sx={{
                width: '2rem',
                height: '2rem',
                color: theme.palette.common.black,
              }}
              onClick={() => formStore.setToggleRampingOff()}
              aria-label="close-winback"
              data-cy="winback-close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {/* Inset 19px each side per the design — not a full-bleed border on the padded row. */}
          <Box
            sx={{
              mx: '19px',
              borderBottom: `1px solid ${theme.palette.brandGray.light}`,
            }}
          />
        </Box>
      )}

      <Stack
        alignItems="center"
        justifyContent="flex-start"
        flexGrow={1}
        spacing={0}
        sx={{
          px: 3,
          pt: { xs: stackGap, md: desktopTopPad },
          pb: { xs: '19px', md: '40px' },
          textAlign: 'center',
        }}
      >
        <Box
          component="img"
          src={ShieldIconContainer}
          alt=""
          sx={{ width: iconSize, height: iconSize, flexShrink: 0 }}
        />

        <Typography
          sx={{
            mt: { xs: stackGap, md: '20px' },
            maxWidth: { xs: '345px', md: isResultsVariant ? '760px' : '590px' },
            fontSize: { xs: '24px', md: '32px' },
            fontWeight: { xs: theme.fontWeights.semiBold, md: '700' },
            lineHeight: { xs: '31.2px', md: desktopTitleLineHeight },
            color: {
              xs: theme.palette.brandDarkBlue.main,
              md: theme.palette.custom.winBackTitle,
            },
            whiteSpace: 'pre-line',
          }}
        >
          {t(
            isResultsVariant ? 'confirmExit.resultsTitle' : 'confirmExit.title'
          )}
        </Typography>

        <Typography
          sx={{
            mt: { xs: '4px', md: '6px' },
            maxWidth: { xs: '277px', md: '760px' },
            fontSize: { xs: '14px', md: '18px' },
            fontWeight: theme.fontWeights.medium,
            lineHeight: { xs: '18.2px', md: '25.2px' },
            color: {
              xs: theme.palette.custom.winBackSubtleText,
              md: theme.palette.custom.winBackBodyText,
            },
          }}
        >
          {t(
            isResultsVariant
              ? 'confirmExit.resultsDescription'
              : 'confirmExit.description'
          )}
        </Typography>

        {isResultsVariant && (
          <Box
            sx={{
              mt: { xs: '20px', md: '31px' },
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <WinBackSavingsCard />
          </Box>
        )}

        <Stack
          direction={isDesktop ? 'row' : 'column'}
          alignItems="center"
          justifyContent="center"
          spacing="14px"
          sx={{
            mt: { xs: stackGap, md: '40px' },
            width: { xs: '100%', md: 'auto' },
            maxWidth: { xs: '345px', md: 'none' },
          }}
        >
          {isDesktop ? (
            <>
              {exitWithoutSaving}
              {primaryButton}
            </>
          ) : (
            <>
              {primaryButton}
              {exitWithoutSaving}
            </>
          )}
        </Stack>
      </Stack>
    </Box>
  );
});
